/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginService } from './storage/login.service';

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag?: (...args: any[]) => void;
    }
}

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private measurementId = environment.gaMeasurementId;
    private isBrowser = false;

    constructor(
        @Inject(PLATFORM_ID) private platformID: object,
        private router: Router,
        private loginStorage: LoginService,
        private zone: NgZone,
    ) {
        this.isBrowser = isPlatformBrowser(platformID);
    }

    public initialize(): void {
        if (!this.isBrowser) {
            return;
        }

        this.injectGtag();
        this.trackPageViews();
    }

    public setUserProperties(): void {
        if (!this.canTrack()) {
            return;
        }

        const userId = this.loginStorage.getUserId() || null;
        const companyIndustry = this.loginStorage.getCompanyIndustry() || null;

        this.zone.runOutsideAngular(() => {
            window.gtag?.('set', 'user_properties', {
                user_id: userId,
                company_industry: companyIndustry,
            });
        });
    }

    public trackProduct(productId: string, productName: string): void {
        if (!this.canTrack()) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            window.gtag?.('event', 'view_item', {
                items: [
                    {
                        item_id: productId,
                        item_name: productName,
                    },
                ],
            });
        });
    }

    public trackCheckout(productId: string, productName: string, productCategory: string, isExtension = false): void {
        if (!this.canTrack()) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            window.gtag?.('event', 'begin_checkout', {
                items: [
                    {
                        item_id: productId,
                        item_name: productName,
                        item_category: productCategory,
                        checkout_type: isExtension ? 'extend' : 'new',
                    },
                ],
            });
        });
    }

    public trackPurchase(payload: Ga4PurchaseEvent): void {
        if (!this.canTrack()) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            window.gtag?.('event', 'purchase', payload);
        });
    }

    private injectGtag(): void {
        if (!this.isBrowser) {
            return;
        }

        if (document.getElementById('ga-gtag-script')) {
            return;
        }

        const gtagScript = document.createElement('script');
        gtagScript.id = 'ga-gtag-script';
        gtagScript.async = true;
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
        document.head.appendChild(gtagScript);

        const inlineScript = document.createElement('script');
        inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${this.measurementId}', { send_page_view: false });
            `;
        document.head.appendChild(inlineScript);

        this.setUserProperties();
    }

    private trackPageViews(): void {
        if (!this.isBrowser) {
            return;
        }

        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (!this.canTrack()) {
                    return;
                }

                this.zone.runOutsideAngular(() => {
                    window.gtag?.('event', 'page_view', {
                        page_path: event.urlAfterRedirects,
                        page_location: window.location.href,
                        page_title: document.title,
                    });
                });
            });
    }

    private canTrack(): boolean {
        return this.isBrowser && !!this.measurementId && typeof window.gtag === 'function';
    }
}

interface Ga4PurchaseEvent {
    transaction_id: string;
    value: number;
    currency: string;
    items: Ga4PurchaseItem[];
}

interface Ga4PurchaseItem {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
    checkout_type?: string;
}
